/* ============================================================
   ÉTAPE 3: TYPES D'ÉVÉNEMENTS (Refactorisée)
   ============================================================ */

import { Plus } from 'lucide-react';
import { SaveButton } from '../SaveButton';
import { EventTypeTemplates } from '../EventTypeTemplates';
import { EventTypeItem } from '../EventTypeItem';
import { EventType } from '../../../../types/event-types.types';

interface Step3EventTypesProps {
  data: any;
  onChange: (updates: any) => void;
  onSave?: () => void;
  isSaving?: boolean;
}

export function Step3EventTypes({ data, onChange, onSave, isSaving }: Step3EventTypesProps) {
  const moduleEnabled = !!data.eventTypesEnabled;
  const requireObjectives = !!data.requireEventObjectives;
  const objectivesLabel = data.eventObjectivesLabel || "Objectifs de l'événement";
  const eventTypes: EventType[] = data.eventTypes || [];

  const addEventType = (template?: { label: string; description: string }) => {
    const newType: EventType = { id: `event_${Date.now()}`, label: template?.label || '', description: template?.description || '', order: eventTypes.length };
    onChange({ eventTypes: [...eventTypes, newType] });
  };

  const updateEventType = (id: string, updates: Partial<EventType>) => {
    onChange({ eventTypes: eventTypes.map((et) => (et.id === id ? { ...et, ...updates } : et)) });
  };

  const deleteEventType = (id: string) => {
    onChange({ eventTypes: eventTypes.filter((et) => et.id !== id) });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
      <ModuleActivation data={data} onChange={onChange} moduleEnabled={moduleEnabled} />

      {moduleEnabled && (
        <div style={{ animation: 'fadeIn 0.3s ease' }}>
          <ObjectivesConfig requireObjectives={requireObjectives} objectivesLabel={objectivesLabel} onChange={onChange} />
          <EventTypeTemplates onAdd={addEventType} />
          
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h4 style={{ fontSize: '0.95rem', fontWeight: 600, color: '#00303c', margin: 0 }}>Types d'événements configurés</h4>
              <button onClick={() => addEventType()} style={addBtnStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#00303c'} onMouseLeave={(e) => e.currentTarget.style.background = '#00758d'}><Plus size={16} /> Ajouter un type</button>
            </div>

            {eventTypes.length === 0 ? (
              <div style={emptyStateStyle}>Aucun type d'événement configuré. Cliquez sur un template ou sur "Ajouter un type".</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {eventTypes.map((et) => <EventTypeItem key={et.id} eventType={et} onUpdate={updateEventType} onDelete={deleteEventType} />)}
              </div>
            )}
          </div>
        </div>
      )}

      {onSave && <SaveButton onSave={onSave} isSaving={isSaving || false} />}
    </div>
  );
}

function ModuleActivation({ moduleEnabled, onChange }: any) {
  return (
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', padding: '1.25rem', background: moduleEnabled ? 'rgba(0,117,141,0.05)' : 'white', border: `2px solid ${moduleEnabled ? '#00758d' : '#e5e7eb'}`, borderRadius: '12px', cursor: 'pointer', transition: 'all 0.2s ease' }}>
      <input type="checkbox" checked={moduleEnabled} onChange={(e) => onChange({ eventTypesEnabled: e.target.checked })} style={{ width: '20px', height: '20px', marginTop: '2px', cursor: 'pointer', accentColor: '#00758d' }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '1rem', fontWeight: 600, color: '#00303c', marginBottom: '0.25rem' }}>Activer les types d'événements</div>
        <div style={{ fontSize: '0.9rem', color: '#666' }}>Permet aux clients de sélectionner le type d'événement lors de la réservation...</div>
      </div>
    </label>
  );
}

function ObjectivesConfig({ requireObjectives, objectivesLabel, onChange }: any) {
  return (
    <div style={{ padding: '1.25rem', background: 'white', border: '2px solid #e5e7eb', borderRadius: '12px', marginBottom: '1.5rem' }}>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', cursor: 'pointer', marginBottom: requireObjectives ? '1rem' : 0 }}>
        <input type="checkbox" checked={requireObjectives} onChange={(e) => onChange({ requireEventObjectives: e.target.checked })} style={{ width: '20px', height: '20px', marginTop: '2px', cursor: 'pointer', accentColor: '#00758d' }} />
        <div style={{ flex: 1 }}><div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#00303c', marginBottom: '0.25rem' }}>Demander les objectifs de l'événement</div><div style={{ fontSize: '0.85rem', color: '#666' }}>Ajoute un champ texte pour la description des objectifs</div></div>
      </label>
      {requireObjectives && (
        <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#00303c', marginBottom: '0.5rem' }}>Libellé du champ</label>
          <input type="text" value={objectivesLabel} onChange={(e) => onChange({ eventObjectivesLabel: e.target.value })} style={{ width: '100%', padding: '0.75rem', fontSize: '0.9rem', border: '1px solid #e5e7eb', borderRadius: '8px', outline: 'none' }} />
        </div>
      )}
    </div>
  );
}

const addBtnStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', background: '#00758d', color: 'white', border: 'none', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s ease' };
const emptyStateStyle: React.CSSProperties = { padding: '2rem', textAlign: 'center', background: '#f8fafb', border: '2px dashed #e5e7eb', borderRadius: '12px', color: '#6b7280' };
